// @ts-nocheck
// Exact live version restored from the production sourcemap (portal.rebels.io, 2026-10-23).
import { jsx as _jsx, jsxs as _jsxs } from 'react/jsx-runtime';
import { useEffect, useMemo, useRef, useState } from 'react';
import { burstSuccessSpark } from '@ui/components/common/micro-interactions';
import { Alert, Box, Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import { CloseCircle } from 'iconsax-react';
import { WidgetHeader } from '../../../../components/common/header/widgetHeader/WidgetHeader';
import { black300, black500 } from '../../../../constants/color';
import CustomSelect from '@ui/components/common/inputs/CustomSelect';
import { QInput } from '@ui/components/common/inputs/QInput';
import { PrimaryButon } from '@ui/components/common/button/PrimaryButon';
import { useAppDispatch } from '@application/store/useStore';
import { Controller, useForm } from 'react-hook-form';
import { translate } from '@application/common/helpers/translate';
import { formatDisplayDate } from '../hours-dates';
import { registerHoursPerDayAsync } from '@application/features/HoursRegistration/hoursRegistrationThunk';
import { getTimeSheetCountsByStatusAsync } from '@application/features/timeSheet/timeSheetThunk';
import { toRegistrationContextWireType } from '@application/features/HoursRegistration/models/registration-context';
import HoursRegistrationApi from '@infrastructure/hours-registration-api';
import { getErrorPayload } from '@application/common/helpers/api-helper';
import { hourWriteRefreshMessage } from '@application/features/HoursRegistration/hour-write-safety';
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
const WORKDAY_SYSTEM_CODE = 'WORKDAY';
const asApiError = (error) => {
    if (error && typeof error === 'object' && typeof error.status === 'number')
        return error;
    return getErrorPayload(error);
};
export const EditHour = ({ open, handleClose, selectedRow, week, context, allDailyHoursOfWeek, onSaved }) => {
    const dispatch = useAppDispatch();
    const [hourTypes, setHourTypes] = useState([]);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [requiresRefresh, setRequiresRefresh] = useState(false);
    const requestInFlight = useRef(false);
    const outcomeUncertain = useRef(false);
    const loadedWeekVersion = useRef();
    const { control, handleSubmit, formState: { errors }, reset, watch } = useForm({
        mode: 'onChange',
        shouldFocusError: true,
        defaultValues: { assignmentReasonId: '', hours: 0 }
    });
    useEffect(() => {
        if (outcomeUncertain.current)
            return;
        if (!open || !selectedRow || !context) {
            setSaveError(null);
            return;
        }
        let active = true;
        loadedWeekVersion.current = week.weekVersion || undefined;
        const missingMetadata = !loadedWeekVersion.current || !selectedRow.id;
        setRequiresRefresh(missingMetadata);
        const load = async () => {
            setIsLoadingTypes(true);
            setSaveError(missingMetadata ? 'This page is missing the current week version. Reload the page before saving hours.' : null);
            try {
                const types = (await HoursRegistrationApi.getHourTypesForContext({
                    contextType: context.contextType,
                    contextId: context.contextId,
                    date: selectedRow.date
                })).data;
                if (!active)
                    return;
                setHourTypes(types);
                const selectedReason = selectedRow.assignmentReasonId || '';
                const defaultReason = context.contextType === 'ClientAssignment'
                    ? types.find((type) => type.systemCode === WORKDAY_SYSTEM_CODE)?.id || types[0]?.id || ''
                    : types[0]?.id || '';
                reset({
                    assignmentReasonId: selectedReason || defaultReason,
                    hours: selectedRow.hours
                });
            }
            catch (error) {
                if (!active)
                    return;
                setHourTypes([]);
                const apiError = asApiError(error);
                const reference = apiError.correlationId ? ` Reference: ${apiError.correlationId}.` : '';
                setSaveError(`Available hour types could not be loaded.${reference}`);
            }
            finally {
                if (active)
                    setIsLoadingTypes(false);
            }
        };
        void load();
        return () => {
            active = false;
        };
    }, [context, open, reset, selectedRow, week.weekVersion]);
    const selectedReasonId = watch('assignmentReasonId');
    const selectedType = hourTypes.find((type) => type.id === selectedReasonId);
    const selectedStoredTypeMissing = Boolean(selectedRow?.assignmentReasonId && !hourTypes.some((type) => type.id === selectedRow.assignmentReasonId));
    const selectOptions = useMemo(() => {
        const options = hourTypes.map((type) => ({ label: type.displayName, value: type.id }));
        if (selectedStoredTypeMissing && selectedRow?.assignmentReasonId) {
            options.push({
                label: `${selectedRow.displayName || selectedRow.client || 'Previously saved hour type'} · unavailable`,
                value: selectedRow.assignmentReasonId
            });
        }
        return options;
    }, [hourTypes, selectedRow, selectedStoredTypeMissing]);
    const internalDepartmentName = (selectedType?.routingTarget === 'InternalDepartment' && context?.internalHoursDepartmentName) || context?.internalDepartmentCode;
    const routingHint = selectedType
        ? selectedType.routingTarget === 'ClientAssignment'
            ? `Booked on the client project for ${context?.displayName}.`
            : `Booked automatically on Internal Department${internalDepartmentName ? ` (${internalDepartmentName})` : ''}.`
        : null;
    const onSubmit = async (values) => {
        if (!selectedRow ||
            !context ||
            requestInFlight.current ||
            requiresRefresh ||
            outcomeUncertain.current ||
            !loadedWeekVersion.current ||
            !selectedRow.id)
            return;
        setSaveError(null);
        const type = hourTypes.find((hourType) => hourType.id === values.assignmentReasonId);
        if (!type) {
            setSaveError(`${formatDisplayDate(selectedRow.date)} · choose an hour type that is available for this date.`);
            return;
        }
        const numberOfHours = Number(values.hours);
        requestInFlight.current = true;
        setIsSaving(true);
        try {
            await dispatch(registerHoursPerDayAsync({
                id: selectedRow.id,
                expectedWeekVersion: loadedWeekVersion.current,
                year: week.year,
                month: week.month,
                weekNo: week.weekNo,
                assignmentReasonId: type.id,
                assignmentId: context.contextType === 'ClientAssignment' ? context.contextId : EMPTY_GUID,
                contextType: toRegistrationContextWireType(context.contextType),
                contextId: context.contextId,
                hours: numberOfHours,
                date: selectedRow.date
            })).unwrap();
            // Decoration only, after the existing save has succeeded.
            burstSuccessSpark();
            await onSaved();
            await dispatch(getTimeSheetCountsByStatusAsync());
            handleClose();
        }
        catch (error) {
            const apiError = asApiError(error);
            const reference = apiError.correlationId ? ` Reference: ${apiError.correlationId}.` : '';
            const code = apiError.code ? ` (${apiError.code})` : '';
            const refreshMessage = hourWriteRefreshMessage(apiError);
            if (refreshMessage) {
                outcomeUncertain.current = true;
                setRequiresRefresh(true);
                setSaveError(`${refreshMessage}${code}${reference}`);
                return;
            }
            const recoverable = apiError.status === 409 || apiError.status === 422;
            setSaveError(`${formatDisplayDate(selectedRow.date)} · ${type.displayName}: ${recoverable
                ? `the entry could not be saved because the timesheet or routing configuration changed${code}. Your input is still here.`
                : `the entry could not be saved${code}. Your input is still here.`}${reference}`);
        }
        finally {
            requestInFlight.current = false;
            setIsSaving(false);
        }
    };
    if (!selectedRow)
        return null;
    return _jsxs(Dialog, {
        open: open,
        onClose: isSaving || requiresRefresh ? undefined : handleClose,
        maxWidth: false,
        BackdropProps: { sx: { backdropFilter: 'blur(3px)', backgroundColor: 'rgba(74, 74, 74, 0.25)' } },
        PaperProps: { sx: { width: { xs: 'calc(100vw - 24px)', sm: 550 }, maxWidth: '100%' } },
        children: [
            _jsxs(WidgetHeader, {
                background: 'white',
                children: [
                    _jsxs(Box, {
                        minWidth: 0,
                        children: [
                            _jsx(Typography, {
                                fontSize: '20px',
                                color: 'black',
                                noWrap: true,
                                children: context?.displayName || 'Hour registration'
                            }),
                            _jsxs(Typography, {
                                variant: 'caption',
                                color: black300,
                                children: [formatDisplayDate(selectedRow.date), ' \u00B7 Week ', week.weekNo]
                            })
                        ]
                    }),
                    _jsx(IconButton, {
                        onClick: handleClose,
                        disabled: isSaving || requiresRefresh,
                        'aria-label': 'Close edit hours',
                        children: _jsx(CloseCircle, { style: { color: 'black' } })
                    })
                ]
            }),
            _jsxs(Box, {
                sx: { p: 3 },
                children: [
                    saveError && _jsx(Alert, { severity: 'error', sx: { mb: 2 }, children: saveError }),
                    requiresRefresh &&
                        _jsxs(Box, {
                            sx: { mb: 2 },
                            children: [
                                _jsx(Button, {
                                    onClick: () => window.location.reload(),
                                    variant: 'outlined',
                                    children: 'Reload page to check saved hours'
                                }),
                                _jsx(Typography, {
                                    variant: 'body2',
                                    sx: { mt: 1 },
                                    children: 'Reloading clears this draft. Review the saved hours for this week before entering anything again.'
                                })
                            ]
                        }),
                    selectedStoredTypeMissing &&
                        _jsx(Alert, {
                            severity: 'warning',
                            sx: { mb: 2 },
                            children: 'The saved hour type is still visible, but it is no longer available for this date. Choose an available type before saving.'
                        }),
                    _jsxs(Box, {
                        sx: { display: 'flex', flexDirection: 'column', gap: 2 },
                        children: [
                            _jsxs(Box, {
                                children: [
                                    _jsx(Typography, { variant: 'subtitle2', color: black500, mb: 0.5, children: 'Registration' }),
                                    isLoadingTypes
                                        ? _jsx(Box, {
                                            sx: { minHeight: 50, display: 'grid', placeItems: 'center' },
                                            children: _jsx(CircularProgress, { size: 22 })
                                        })
                                        : _jsx(Controller, {
                                            name: 'assignmentReasonId',
                                            control: control,
                                            rules: {
                                                required: { value: true, message: translate('validations.fieldIsRequired') },
                                                validate: (value) => hourTypes.some((type) => type.id === value) ||
                                                    'Choose an hour type available for this date.'
                                            },
                                            render: ({ field }) => _jsx(CustomSelect, {
                                                options: selectOptions,
                                                selectedOption: field.value,
                                                label: selectOptions.length ? 'Registration' : 'No hour types available',
                                                disabled: !selectOptions.length || isSaving || requiresRefresh,
                                                handleChange: (event) => field.onChange(event.target.value),
                                                errormsg: errors.assignmentReasonId?.message
                                            })
                                        }),
                                    routingHint &&
                                        _jsx(Typography, {
                                            variant: 'caption',
                                            color: black300,
                                            sx: { display: 'block', mt: 1 },
                                            children: routingHint
                                        })
                                ]
                            }),
                            _jsx(Controller, {
                                name: 'hours',
                                control: control,
                                rules: {
                                    validate: (value) => {
                                        const normalized = String(value ?? '').trim();
                                        if (normalized === '')
                                            return translate('validations.fieldIsRequired');
                                        if (normalized.includes('.'))
                                            return translate('validations.invalidDecimalWholeNumber');
                                        const numeric = Number(normalized);
                                        if (!Number.isInteger(numeric) || numeric < 0)
                                            return translate('validations.invalidWholeNumber');
                                        if (numeric > 17)
                                            return translate('validations.maximumIntegerAllowed', { count: 18 });
                                        const otherHours = allDailyHoursOfWeek
                                            .filter((entry) => entry.date === selectedRow.date &&
                                            entry.id !== selectedRow.id &&
                                            entry.systemCode !== 'NO_EXPORT')
                                            .reduce((sum, entry) => sum + Number(entry.hours), 0);
                                        const selectedHours = selectedType?.exportPolicy === 'NeverExport' ? 0 : numeric;
                                        if (otherHours + selectedHours > 17) {
                                            return translate('validations.maximumHoursAllowedInADay', {
                                                date: formatDisplayDate(selectedRow.date),
                                                count: 17
                                            });
                                        }
                                        return true;
                                    }
                                },
                                render: ({ field }) => _jsx(QInput, {
                                    ...field,
                                    label: 'Hours',
                                    errormsg: errors.hours?.message,
                                    disabled: isSaving || isLoadingTypes || requiresRefresh
                                })
                            }),
                            _jsx(Box, {
                                mt: 1,
                                children: _jsx(PrimaryButon, {
                                    type: 'button',
                                    onClick: handleSubmit(onSubmit),
                                    disabled: isSaving || isLoadingTypes || requiresRefresh || hourTypes.length === 0,
                                    children: isSaving ? 'Saving hours…' : 'Save hours'
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
